/**
 * Overlay / scroll-lock guard.
 *
 * Radix (dropdown-menu, dialog, sheet) uses `react-remove-scroll` which sets
 * `pointer-events: none` on <body> and adds `data-scroll-locked` while a modal
 * overlay is open. If the overlay unmounts mid-animation — which happens when a
 * menu item triggers a route change, or when iOS WKWebView freezes/resumes the
 * JS context while the menu is open — the cleanup never runs and <body> keeps
 * `pointer-events: none` forever. Visually nothing changes, but every tap
 * (including the hamburger button) is swallowed until the app is force-closed.
 *
 * `releaseStaleOverlays()` is a pure DOM cleanup: it only acts when NO overlay
 * is actually open, so it can never break a legitimately open menu/dialog.
 */

const OPEN_SELECTORS = [
  "[data-state='open'][role='dialog']",
  "[data-state='open'][role='alertdialog']",
  "[data-state='open'][role='menu']",
  "[data-state='open'][data-radix-dialog-content]",
  "[data-radix-popper-content-wrapper] [data-state='open']",
].join(",");

const STALE_OVERLAY_SELECTORS = [
  "[data-radix-focus-guard]",
  "[data-radix-portal]:empty",
  "[data-radix-popper-content-wrapper]",
  "[data-state='closed'][role='dialog']",
  "[data-state='closed'][role='alertdialog']",
  "[data-state='closed'][role='menu']",
  "[data-state='closed'][data-radix-dialog-content]",
  "[data-state='closed'][data-radix-dialog-overlay]",
].join(",");

const LOCKED_STYLE_PROPS = ["pointer-events", "overflow"] as const;

function describeElement(element: Element | null): string | null {
  if (!element) return null;
  const id = element.id ? `#${element.id}` : "";
  const classes =
    typeof element.className === "string" && element.className.trim()
      ? `.${element.className.trim().split(/\s+/).slice(0, 4).join(".")}`
      : "";
  const role = element.getAttribute("role");
  const state = element.getAttribute("data-state");
  const trigger = element.getAttribute("data-menu-trigger");
  return [
    element.tagName.toLowerCase() + id + classes,
    role ? `role=${role}` : null,
    state ? `state=${state}` : null,
    trigger ? `menu=${trigger}` : null,
  ]
    .filter(Boolean)
    .join(" ");
}

function removeLockFromElement(element: HTMLElement | null): boolean {
  if (!element) return false;
  let released = false;

  LOCKED_STYLE_PROPS.forEach((prop) => {
    const value = element.style.getPropertyValue(prop);
    if (value === "none" || value === "hidden" || value === "clip") {
      element.style.removeProperty(prop);
      released = true;
    }
  });

  if (element.getAttribute("aria-hidden") === "true") {
    element.removeAttribute("aria-hidden");
    released = true;
  }
  if (element.hasAttribute("inert")) {
    element.removeAttribute("inert");
    released = true;
  }

  return released;
}

/**
 * True when React still owns this node (it was rendered by a mounted fiber,
 * typically inside a Radix portal). Removing such a node manually corrupts
 * React's DOM bookkeeping and throws on the next commit.
 */
export function isReactOwned(node: Node): boolean {
  for (const key in node) {
    if (
      key.startsWith("__reactFiber$") ||
      key.startsWith("__reactProps$") ||
      key.startsWith("__reactContainer$") ||
      key.startsWith("__reactInternalInstance$")
    ) {
      return true;
    }
  }
  // A node rendered by React into a portal container: the container itself
  // carries the container key.
  const parent = node.parentNode as unknown as Record<string, unknown> | null;
  if (parent) {
    for (const key in parent) {
      if (key.startsWith("__reactContainer$")) return true;
    }
  }
  return false;
}

export function hasOpenOverlay(): boolean {
  if (typeof document === "undefined") return false;
  return !!document.querySelector(OPEN_SELECTORS);
}


export function getOverlayDebugSnapshot(label = "overlay-snapshot", x?: number, y?: number) {
  if (typeof document === "undefined") return { label, unavailable: true };
  const root = document.getElementById("root") as HTMLElement | null;
  const html = document.documentElement;
  const body = document.body;
  const topElement =
    typeof x === "number" && typeof y === "number" ? document.elementFromPoint(x, y) : null;

  const overlays = Array.from(
    document.querySelectorAll(
      [
        OPEN_SELECTORS,
        STALE_OVERLAY_SELECTORS,
        "[data-radix-portal]",
        "[data-menu-trigger]",
        "#native-splash",
      ].join(","),
    ),
  )
    .slice(0, 20)
    .map(describeElement);

  return {
    label,
    route: window.location.pathname + window.location.search,
    visibility: document.visibilityState,
    point: typeof x === "number" && typeof y === "number" ? { x, y } : null,
    topElement: describeElement(topElement),
    openOverlay: hasOpenOverlay(),
    html: {
      pointerEvents: html.style.pointerEvents || getComputedStyle(html).pointerEvents,
      overflow: html.style.overflow || getComputedStyle(html).overflow,
      ariaHidden: html.getAttribute("aria-hidden"),
      inert: html.hasAttribute("inert"),
    },
    body: {
      pointerEvents: body.style.pointerEvents || getComputedStyle(body).pointerEvents,
      overflow: body.style.overflow || getComputedStyle(body).overflow,
      dataScrollLocked: body.getAttribute("data-scroll-locked"),
      ariaHidden: body.getAttribute("aria-hidden"),
      inert: body.hasAttribute("inert"),
    },
    root: root
      ? {
          pointerEvents: root.style.pointerEvents || getComputedStyle(root).pointerEvents,
          overflow: root.style.overflow || getComputedStyle(root).overflow,
          ariaHidden: root.getAttribute("aria-hidden"),
          inert: root.hasAttribute("inert"),
        }
      : null,
    overlays,
  };
}

export function logOverlayDebug(label: string, event?: Event, extra?: Record<string, unknown>) {
  if (typeof document === "undefined") return;
  let x: number | undefined;
  let y: number | undefined;
  const maybePointer = event as PointerEvent | MouseEvent | undefined;
  if (typeof maybePointer?.clientX === "number" && typeof maybePointer?.clientY === "number") {
    x = maybePointer.clientX;
    y = maybePointer.clientY;
  } else if (event && "touches" in event) {
    const touch = (event as TouchEvent).touches[0] || (event as TouchEvent).changedTouches[0];
    if (touch) {
      x = touch.clientX;
      y = touch.clientY;
    }
  }

  try {
    console.info("🍔 [MenuDiag]", getOverlayDebugSnapshot(label, x, y), extra || {});
  } catch {
    /* noop */
  }
}

export function scheduleStaleOverlayRelease(label = "scheduled-release", delays = [0, 80, 350, 1000]) {
  if (typeof window === "undefined") return () => undefined;
  const timers = delays.map((delay) =>
    window.setTimeout(() => {
      releaseStaleOverlays(label);
    }, delay),
  );
  return () => timers.forEach((timer) => window.clearTimeout(timer));
}

/** Returns true when it had to release a stale lock. */
export function releaseStaleOverlays(label = "releaseStaleOverlays"): boolean {
  if (typeof document === "undefined") return false;
  if (hasOpenOverlay()) return false;

  const body = document.body;
  if (!body) return false;
  const root = document.getElementById("root") as HTMLElement | null;

  let released = false;

  released = removeLockFromElement(document.documentElement) || released;
  released = removeLockFromElement(body) || released;
  released = removeLockFromElement(root) || released;

  if (body.hasAttribute("data-scroll-locked")) {
    body.removeAttribute("data-scroll-locked");
    released = true;
  }

  const splash = document.getElementById("native-splash");
  if (splash) {
    splash.parentNode?.removeChild(splash);
    released = true;
  }

  // Orphaned focus guards, popper wrappers, closed overlays/backdrops, and
  // aria-hidden wrappers left behind by an interrupted Radix/vaul unmount.
  //
  // CRITICAL: only nodes that React does NOT own may be removed. Radix renders
  // the popper wrapper and focus guards through a React portal; ripping one out
  // of the DOM while its fiber is still mounted makes the next React commit
  // (i.e. reopening the menu) call removeChild/insertBefore on a node that is no
  // longer a child of its container, throwing
  // "NotFoundError: The node to be removed is not a child of this node"
  // which bubbles to the ErrorBoundary ("Something went wrong").
  // For React-owned nodes we only neutralise the lock attributes.
  document.querySelectorAll(STALE_OVERLAY_SELECTORS).forEach((node) => {
    if (node.matches(OPEN_SELECTORS)) return;
    if (isReactOwned(node)) {
      released = removeLockFromElement(node as HTMLElement) || released;
      return;
    }
    node.parentNode?.removeChild(node);
    released = true;
  });


  if (released) {
    try {
      console.info(
        "🧹 [OverlayGuard] Released stale overlay lock",
        getOverlayDebugSnapshot(label),
      );
    } catch {
      /* noop */
    }
  }

  return released;
}
